/**
 * NBA Consolidated Pricing — Fee-adjusted top-of-book pricing engine
 *
 * Computes display prices for team win markets. Handles maker/taker mode,
 * taker fee application, bid↔ask inversion, and ladder browsing.
 *
 * @ai-context
 *   Role: Canonical pricing engine for NBA/sports value dashboard. Every
 *         team-win price displayed comes through topNoFromLevels or getTeamWinData.
 *   Pattern: Pure functions. BookLevel arrays + mode flags in, prices out.
 *   Purity: No side effects, no API calls.
 *
 * @ai-invariant "Team WINS" = NO on the opponent market. Away wins = NO on
 *   home market, home wins = NO on away market. getTeamWinData does the
 *   opponent lookup — callers pass the team side, not the market side.
 *
 * @ai-invariant applyTakerFee uses perContractFeeCents (canonical formula) to compute
 *   fee on notional 1000 contracts, then spreads per contract and adds to price.
 *   The Math.ceil on feeTotalCents rounds UP (conservative — buyer pays more).
 *
 * @ai-invariant bidToAsk: ask = 100 - bid. Standard binary complement.
 *   In taker mode, topNoFromLevels inverts YES bids → NO asks, then applies fee.
 *   In maker mode, it reads NO bids directly.
 *
 * @ai-upstream
 *   - ./types → BookLevel, TeamSide, Venue, VenueGameBooks
 *
 * @ai-downstream
 *   - orderMath.ts → perContractFeeCents, KALSHI_TAKER_FEE_RATE, POLY_TAKER_FEE_RATE
 *   - arbitrage.ts → perContractFeeCents, KALSHI_TAKER_FEE_RATE, POLY_TAKER_FEE_RATE
 *   - ValueDashboardView → getTeamWinData, liquidityDollars
 *   - ConsolidatedMoneylines, TwoRowMoneylines, InlineBookPanel, GameDetailPanel
 *   - TwoRowSpreads, TwoRowTotals → liquidityDollars
 *   - pricing.test.ts (4 cases — light coverage)
 */

import type { BookLevel, TeamSide, Venue, VenueGameBooks } from './types';

export const KALSHI_TAKER_FEE_RATE = 0.07;
export const POLY_TAKER_FEE_RATE = 0.0;

/**
 * Per-contract taker fee in cents — canonical fee formula (T-006).
 * fee = feeRate × P × (1-P) × 100   where P = priceCents / 100
 *
 * Single source of truth. Used by:
 *   - applyTakerFee (this file) — display pricing with batch rounding
 *   - orderMath.computeCost — trade UX cost/payout in dollars
 *   - arbitrage.takerFeeCents — cross-venue arb spread calculation
 */
export function perContractFeeCents(priceCents: number, feeRate: number): number {
  const P = priceCents / 100;
  return feeRate * P * (1 - P) * 100;
}

export function bidToAsk(bidCents: number | null | undefined): number | null {
  if (bidCents === null || bidCents === undefined) return null;
  const v = Number(bidCents);
  if (!Number.isFinite(v)) return null;
  return 100 - v;
}

/**
 * Mirrors `nba/static/index.html` taker fee approximation.
 * Fee is computed on a notional `contracts` (default 1000) and then spread per contract.
 */
export function applyTakerFee(
  priceCents: number | null,
  feeRate: number,
  contracts = 1000
): number | null {
  if (priceCents === null) return null;
  const p = Number(priceCents);
  if (!Number.isFinite(p) || p <= 0 || p >= 100) return p;

  const feeTotalCents = Math.ceil(perContractFeeCents(p, feeRate) * contracts);
  const feePerContractCents = feeTotalCents / contracts;
  return p + feePerContractCents;
}

export function takerFeeForVenue(venue: Venue): number {
  return venue === 'kalshi' ? KALSHI_TAKER_FEE_RATE : POLY_TAKER_FEE_RATE;
}

export function getEffectivePriceFromBid(args: {
  bidCents: number | null;
  isTakerMode: boolean;
  takerFeeRate: number;
}): number | null {
  const { bidCents, isTakerMode, takerFeeRate } = args;
  if (bidCents === null) return null;
  if (!isTakerMode) return bidCents;
  const ask = bidToAsk(bidCents);
  return applyTakerFee(ask, takerFeeRate);
}

export interface TopResult {
  /** Display price (maker bid or taker ask+fee), cents */
  priceCents: number | null;
  /** Liquidity (contracts) at the selected raw bid */
  liq: number | null;
  /** Raw bid price used (cents), used for order placement */
  rawBidPriceCents: number | null;
}

function bestBid(levels: BookLevel[] | null | undefined): BookLevel | null {
  if (!Array.isArray(levels) || levels.length === 0) return null;
  let best = levels[0];
  for (const lvl of levels) {
    if (lvl && Number(lvl.priceCents) > Number(best.priceCents)) best = lvl;
  }
  return best ?? null;
}

/**
 * Mirrors `topNoFromLevels` from `nba/static/index.html`.
 *
 * Interpretation:
 * - Team win price is computed as **NO on opponent**.
 * - In maker mode, use the opponent NO bid ladder.
 * - In taker mode, use opponent YES bids to infer NO ask (100 - best YES bid), then apply taker fee.
 *
 * Important: in taker mode, if YES ladder is empty, return null (do not fall back).
 */
export function topNoFromLevels(args: {
  noLevels: BookLevel[] | null | undefined;
  yesLevels: BookLevel[] | null | undefined;
  isTakerMode: boolean;
  takerFeeRate: number;
}): TopResult {
  const { noLevels, yesLevels, isTakerMode, takerFeeRate } = args;

  if (!Array.isArray(noLevels) || noLevels.length === 0) {
    return { priceCents: null, liq: null, rawBidPriceCents: null };
  }

  if (isTakerMode) {
    // Mirror nba JS behavior: `if (isTakerMode && yesLevels)` is true even for []
    if (!Array.isArray(yesLevels) || yesLevels.length === 0) {
      return { priceCents: null, liq: null, rawBidPriceCents: null };
    }
    const bestYes = bestBid(yesLevels);
    if (!bestYes) return { priceCents: null, liq: null, rawBidPriceCents: null };
    const noAsk = bidToAsk(bestYes.priceCents);
    const effective = applyTakerFee(noAsk, takerFeeRate);
    return {
      priceCents: effective,
      liq: bestYes.size ?? null,
      rawBidPriceCents: bestYes.priceCents ?? null,
    };
  }

  const bestNo = bestBid(noLevels);
  if (!bestNo) return { priceCents: null, liq: null, rawBidPriceCents: null };
  const effective = getEffectivePriceFromBid({
    bidCents: bestNo.priceCents ?? null,
    isTakerMode,
    takerFeeRate,
  });
  return {
    priceCents: effective,
    liq: bestNo.size ?? null,
    rawBidPriceCents: bestNo.priceCents ?? null,
  };
}

export interface TeamWinData {
  priceCents: number | null;
  liq: number | null;
  rawBidPriceCents: number | null;
  marketTicker: string | null;
  tokenId?: string;
  noLevels: BookLevel[];
  yesLevels: BookLevel[];
}

/**
 * Port of `getTeamWinData()` from `nba/static/index.html`.
 *
 * This returns the price for "TEAM WINS" computed as **NO on the opponent market**.
 * For teamSide=away, opponent is the *home* market.
 */
export function getTeamWinData(args: {
  game: VenueGameBooks | null | undefined;
  venue: Venue;
  teamSide: TeamSide;
  isTakerMode: boolean;
}): TeamWinData {
  const { game, venue, teamSide, isTakerMode } = args;
  if (!game) {
    return {
      priceCents: null,
      liq: null,
      rawBidPriceCents: null,
      marketTicker: null,
      noLevels: [],
      yesLevels: [],
    };
  }

  // @ai-danger: "Team wins" = NO on OPPONENT market. Away→look at home, home→look at away.
  const opp = teamSide === 'away' ? game.markets.home : game.markets.away;
  const noLevels = opp?.no ?? [];
  const yesLevels = opp?.yes ?? [];
  const top = topNoFromLevels({
    noLevels,
    yesLevels,
    isTakerMode,
    takerFeeRate: takerFeeForVenue(venue),
  });

  return {
    priceCents: top.priceCents,
    liq: top.liq,
    rawBidPriceCents: top.rawBidPriceCents,
    marketTicker: opp?.marketTicker ?? null,
    tokenId: opp?.tokenId,
    noLevels,
    yesLevels,
  };
}

export function next3NoLadders(
  noLevels: BookLevel[],
  bestBidPriceCents: number | null
): BookLevel[] {
  if (!Array.isArray(noLevels) || noLevels.length === 0) return [];
  if (bestBidPriceCents === null) return [];
  const below = noLevels.filter((lvl) => Number(lvl.priceCents) < bestBidPriceCents);
  below.sort((a, b) => Number(b.priceCents) - Number(a.priceCents));
  return below.slice(0, 3);
}

export function next3YesLadders(
  yesLevels: BookLevel[],
  bestBidPriceCents: number | null
): BookLevel[] {
  if (!Array.isArray(yesLevels) || yesLevels.length === 0) return [];
  if (bestBidPriceCents === null) return [];
  const below = yesLevels.filter((lvl) => Number(lvl.priceCents) < bestBidPriceCents);
  below.sort((a, b) => Number(b.priceCents) - Number(a.priceCents));
  return below.slice(0, 3);
}

export function liquidityDollars(
  liqContracts: number | null,
  priceCents: number | null
): number | null {
  if (liqContracts === null || priceCents === null) return null;
  // @ai-note: contracts * cents / 100 = dollars. Standard conversion.
  const dollars = (Number(liqContracts) * Number(priceCents)) / 100;
  return Number.isFinite(dollars) ? dollars : null;
}

/**
 * Compute order price from raw bid price.
 * In maker mode, returns the raw bid price.
 * In taker mode, converts bid to ask (100 - bid).
 */
export function computeOrderPriceFromRawBid(
  rawBidPriceCents: number | null,
  isTakerMode: boolean
): number | null {
  if (rawBidPriceCents === null) return null;
  if (!isTakerMode) return rawBidPriceCents;
  return bidToAsk(rawBidPriceCents);
}

/**
 * @ai-modification-checklist
 *
 * Before modifying this file, verify:
 *
 * 1. OPPONENT LOOKUP — "Team wins" = NO on opponent market. Away→home,
 *    home→away. getTeamWinData does the flip. Don't double-invert.
 *
 * 2. FEE MODEL — perContractFeeCents is the canonical formula (T-006).
 *    applyTakerFee, orderMath.computeCost, and arbitrage.takerFeeCents
 *    all delegate to it. Change the formula in ONE place only.
 *
 * 3. FEE RATE CONSTANTS — KALSHI 7%, POLY 0%. Single source here,
 *    imported by orderMath.ts and arbitrage.ts.
 *
 * 4. TAKER VS MAKER — topNoFromLevels switches behavior: taker reads YES
 *    bids and inverts, maker reads NO bids directly. Both paths apply
 *    different fee logic.
 *
 * 5. LIQUIDITY DOLLARS — contracts * cents / 100 = dollars. Standard pattern.
 *
 * 6. DOWNSTREAM — 8+ component files import from here. Changes affect
 *    the entire value dashboard pricing display.
 *
 * 7. TEST FILE — pricing.test.ts (4 cases — light coverage).
 *    Run: npm run dashboard:test -- pricing
 */
